<template>
    <div class="project-options">
        <design-2021-apprentice-multistep-form-wizard-form-header-component
            :title="$t('group.create.options')"
            :description="description">
        </design-2021-apprentice-multistep-form-wizard-form-header-component>

        <div
            class="errors"
            v-if="hasError">
            <b-alert
                class="project-settings-error"
                show
                variant="danger">
                {{ $t('group.create.visibility_not_set_for_ratings') }}
            </b-alert>
        </div>

        <b-list-group class="project-options">
            <b-list-group-item
                class="portfolio-option-item"
                v-for="(option, needle) in options"
                v-if="option.key !== 'ratingsables'"
                :key="`${option.key}`">
                <span class="option-title">{{ option.label }}</span>
                <b-form-checkbox
                    v-model="option.enabled"
                    name="project-options-toggle"
                    @input="toggle(option)"
                    switch>
                </b-form-checkbox>

                <i
                    class="fas fa-fw fa-chevron-down rating-option-collapse-icon"
                    v-if="option.key === 'rating' && option.enabled && ratings.length > 0"
                    :class="ratingsToggleVisibility ? 'up' : 'down'"
                    @click="toggleRatingOptionContent">
                </i>

                <i
                    class="fas fa-fw fa-chevron-down roadmap-option-collapse-icon"
                    v-if="option.key === 'roadmap' && option.enabled && roadmaps.length > 0"
                    :class="roadmapToggleVisibility ? 'up' : 'down'"
                    @click="toggleRoadmapOptionContent">
                </i>

                <b-collapse
                    class="rating-option-collapse"
                    id="rating-option-collapse"
                    v-model="ratingsToggleVisibility"
                    v-if="option.key === 'rating' && option.enabled && ratings.length > 0">
                    <div class="description">{{ $t('group.create.select_rating_forms_explanation') }}</div>

                    <design-2021-apprentice-data-table
                        :fields="ratingFields"
                        :items="ratings"
                        nosearch>
                        <template #cell(rating_id)="data">
                            <div class="rating-name">
                                {{ data.item.title }}
                            </div>
                        </template>

                        <template #cell(custom_title)="data">
                            <div class="rating-custom-name">
                                <design-2021-group-show-rating-custom-title-input-component
                                    v-model="data.item.custom_title"
                                    :key="data.item.key">
                                </design-2021-group-show-rating-custom-title-input-component>
                            </div>
                        </template>

                        <template #cell(visibility)="data">
                            <b-form-group>
                                <b-form-radio-group
                                    class="rating-visibility"
                                    v-model="data.item.visibility"
                                    :name="$t('group.create.visibility')"
                                    buttons>
                                    <b-form-radio
                                        v-b-tooltip.hover
                                        v-for="option in ratingVisibilityOptions"
                                        :value="option.value"
                                        :title="option.tool_tip"
                                        :key="option.value">
                                    </b-form-radio>
                                </b-form-radio-group>
                            </b-form-group>
                        </template>

                        <template #cell(toggled)="data">
                            <div class="rating-toggled">
                                <b-form-checkbox
                                    v-model="data.item.enabled"
                                    name="rating-item-toggle"
                                    switch>
                                </b-form-checkbox>
                            </div>
                        </template>
                    </design-2021-apprentice-data-table>
                </b-collapse>

                <b-collapse
                    class="roadmap-option-collapse"
                    id="roadmap-option-collapse"
                    v-model="roadmapToggleVisibility"
                    v-if="option.key === 'roadmap' && option.enabled && roadmaps.length > 0">
                    <div class="description">{{ $t('group.create.choose_roadmap_explanation') }}</div>

                    <div class="roadmap-options">
                        <design2021-apprentice-multiselect-component
                            class="roadmap-options-select"
                            v-model="linkedRoadmap"
                            label="title"
                            track-by="id"
                            :placeholder="$t('group.create.choose_roadmap')"
                            :options="roadmaps"
                            :allow-empty="true"
                            :multiple-select="false"
                            :disabled="!option.enabled">
                        </design2021-apprentice-multiselect-component>
                    </div>
                </b-collapse>
            </b-list-group-item>
        </b-list-group>
    </div>
</template>

<script>
import Design2021ApprenticeMultiselectComponent from '../../../../common/design-2021-apprentice-multiselect-component.vue';
import $ from 'jquery';

export default {
    name: 'design-2021-apprentice-multistep-wizard-project-options-step-component',
    components: { Design2021ApprenticeMultiselectComponent },
    props: {
        value: Array,
        roadmapsIn: {
            type: Array,
            required: true,
        },

        ratingsIn: {
            type: Array,
            required: true,
        },

        settingsIn: {
            type: Object,
            required: true,
            default: () => {},
        },
    },

    data() {
        return {
            description: this.$t('group.create.options_explanation'),
            options: this.value ? this.value : {},
            linkedRoadmap: this.getLinkedRoadmaps(),
            roadmapToggleVisibility: false,
            ratingsToggleVisibility: false,
            ratingFields: [
                {
                    key: 'rating_id',
                    label: this.$t('group.create.form'),
                },
                {
                    key: 'custom_title',
                    label: this.$t('group.create.name'),
                },
                {
                    key: 'visibility',
                    label: this.$t('group.create.visibility'),
                },
                {
                    key: 'toggled',
                    label: this.$t('group.create.linked'),
                },
            ],
            ratingVisibilityOptions: [
                {
                    text: this.$t('group.create.everyone'),
                    value: 1,
                    tool_tip: this.$t('group.create.visible_for_everyone'),
                },
                {
                    text: this.$t('group.create.teachers_only'),
                    value: 2,
                    tool_tip: this.$t('group.create.visible_for_teachers_only'),
                },
            ],
            hasError: false,
        };
    },

    mounted() {
        for (const option of this.options) {
            if (option.key === 'rating' && option.enabled) {
                this.ratingsToggleVisibility = true;
            }

            if (option.key === 'roadmap' && option.enabled) {
                this.roadmapToggleVisibility = true;
            }
        }
    },

    methods: {
        toggle(option) {
            if (option.key === 'rating') {
                this.ratingsToggleVisibility = true;
            }

            if (option.key === 'roadmap') {
                this.roadmapToggleVisibility = true;
            }
        },

        toggleRatingOptionContent() {
            this.ratingsToggleVisibility = !this.ratingsToggleVisibility;
        },

        toggleRoadmapOptionContent() {
            this.roadmapToggleVisibility = !this.roadmapToggleVisibility;
        },

        async checkForErrors() {
            const ratingIndex = this.options.map((option) => option.key).indexOf('rating');
            const ratingsablesIndex = this.options.map((option) => option.key).indexOf('ratingsables');

            if (ratingIndex !== -1) {
                if (this.options[ratingIndex].enabled) {
                    for (const ratingsable of this.options[ratingsablesIndex].ratingsables) {
                        if (ratingsable.enabled && ratingsable.visibility === null) {
                            this.hasError = true;
                            $('.main-content').animate({ scrollTop: 0 }, 500);
                            const response = true;
                            return await response;
                        }
                    }
                }
            }
        },

        getLinkedRoadmaps() {
            const index = this.value.map((option) => option.key).indexOf('roadmap');
            if (index === -1) {
                return;
            }

            return this.value[index].linked_roadmap;
        },
    },

    computed: {
        ratings() {
            const index = this.options.map((option) => option.key).indexOf('ratingsables');
            if (index === -1) {
                return [];
            }

            this.options[index].ratingsables = this.ratingsIn
                .filter((rating) => rating.course_id === this.settingsIn.course.id)
                .map((rating) => {
                    return {
                        rating_id: rating.id,
                        title: rating.title,
                        custom_title: '',
                        visibility: null,
                        enabled: false,
                    };
                });

            return this.options[index].ratingsables;
        },

        roadmaps() {
            return this.roadmapsIn.filter((roadmap) => roadmap.course_id === this.settingsIn.course.id);
        },
    },

    watch: {
        linkedRoadmap: function (newVal) {
            const index = this.value.map((option) => option.key).indexOf('roadmap');
            if (index === -1) {
                return null;
            }

            this.options[index].linked_roadmap = { ...newVal };
        },
    },
};
</script>

<style scoped lang="scss">
.list-group-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: none;
}

.portfolio-option-item {
    padding: map-get($grid-gutter-spacing, 'eight') 0;
}

.rating-option-collapse,
.roadmap-option-collapse {
    flex: 1 0 100%;
    padding: map-get($grid-gutter-spacing, 'twenty-one');

    .description {
        color: $text-muted-color;
        font-weight: $font-weight-medium;
        line-height: 1.5;
        margin-bottom: map-get($grid-gutter-spacing, 'ten');
    }
}

.rating-visibility ::v-deep {
    label {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        flex: 0 1 auto;
        background-color: transparent;
        border-color: transparent;

        span {
            display: none;
        }

        &,
        &:not(:last-child):not(.dropdown-toggle),
        &:not(:first-child) {
            border-top-right-radius: 4px;
            border-bottom-right-radius: 4px;
            border-top-left-radius: 4px;
            border-bottom-left-radius: 4px;
        }

        &:before,
        &:after {
            color: var(--primary);
            content: '';
            display: block;
            font-family: 'Font Awesome 5 Pro';
            font-weight: $font-weight-bolder;
        }

        &:before {
            position: absolute;
        }

        &:after {
            opacity: 0.4;
        }

        &:first-child {
            margin-right: map-get($grid-gutter-spacing, 'fifteen');

            &:before {
                content: '\f0c0';
            }

            &:after {
                content: '\10f0c0';
            }
        }

        &:nth-child(2) {
            &:before {
                content: '\f007';
            }

            &:after {
                content: '\10f007';
            }
        }
    }

    label:not(.active):hover {
        background-color: var(--primary);

        &:before,
        &:after {
            color: $white;
        }
    }

    label:not(:disabled):not(.disabled).active,
    label:not(:disabled):not(.disabled).active:focus {
        background-color: transparent;
        border-color: var(--primary);
        box-shadow: none;
    }
}

.rating-option-collapse-icon,
.roadmap-option-collapse-icon {
    transition: all 0.8s ease;
    margin-left: auto;
    transform: rotate(0deg);
    transform-origin: center;

    &.up {
        transform: rotate(180deg);
    }

    &.down {
        transform: rotate(0deg);
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .portfolio-option-item {
        flex-wrap: wrap;
        justify-content: flex-start;
        column-gap: map-get($grid-gutter-spacing, 'sixty');

        .option-title {
            width: 150px;
        }
    }
}
</style>
